import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect, useRef, forwardRef } from "react";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import "./Dropdown.css";
import { Col, Container, Row, Form } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";

export type DropdownProps =  {
  options: { id: number; value: string; label: string }[];
  onChange?: (id: number) => void;
  headerPlaceholder: string;
  label?: string;
  boldLabel?: boolean;
  width?: string;
  height?: string;
  name: string;
  required?: boolean;
  tabIndex?: number;
};

const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(function Dropdown(
  {
    name,
    options,
    onChange,
    width = "auto",
    height = "auto",
    headerPlaceholder,
    required,
    label,
    tabIndex,
    boldLabel,
  },
  ref
) {
  const [isOpen, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const { control, watch } = useFormContext();

  // scroll to selected item when opened
  const currentValue = watch(name) as string | undefined;
  useEffect(() => {
    if (!isOpen) return;
    const idx = options.findIndex((o) => o.value === (currentValue ?? ""));
    const el = idx >= 0 ? dropdownItemsRef.current[idx] : null;
    el?.scrollIntoView({ behavior: "smooth" });
    el?.focus?.();
  }, [isOpen, currentValue, options]);

  // Close on outside click
  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const selected = options.find((o) => o.value === (field.value ?? ""));
        const selectedId = selected?.id ?? null;
        const selectedLabel = selected?.label ?? headerPlaceholder;

        const toggle = () => setOpen((o) => !o);

        const selectById = (id: number, close = true) => {
          const next = options.find((o) => o.id === id)?.value ?? "";
          field.onChange(next);
          onChange?.(id);
          if (close) setOpen(false);
        };

        // Current selected index or -1 if no option is selected yet
        const selectedIndex = options.findIndex(o => o.value === (field.value ?? ""));

        // Move selection by +/- 1
        const moveBy = (changeValue: number) => {
          if (!options.length) return;
          const base = selectedIndex < 0 ? 0 : selectedIndex;
          const nextIdx = base + changeValue;
          const safeNextIdx = Math.min(Math.max(nextIdx, 0), options.length - 1);
          const next = options[safeNextIdx];
          if (!next) return;
          field.onChange(next.value);
          const el = dropdownItemsRef.current[safeNextIdx];
          el?.scrollIntoView();
        };

        // Keyboard handling navigation
        const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
          const k = e.key;

          if (k === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            moveBy(1);
            return;
          }

          if (k === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            moveBy(-1);
            return;
          }

          // search forward from the next item; WRAP to top if needed
          if (/^[a-z]$/i.test(k)) {
            e.preventDefault();
            const letter = k.toLowerCase();
            if (!options.length) return;
            const start = Math.max(selectedIndex, -1) + 1;
            const len = options.length;
            let foundIdx = -1;
            for (let i = 0; i < len; i++) {
              const idx = (start + i) % len;
              if (options[idx].label.toLowerCase().startsWith(letter)) {
                foundIdx = idx;
                break;
              }
            }
            if (foundIdx >= 0) {
              field.onChange(options[foundIdx].value);
              setOpen(true);
              dropdownItemsRef.current[foundIdx]?.scrollIntoView({ behavior: "smooth" });
            }
            return;
          }

          if (k === "Enter") {
            e.preventDefault();
            setOpen(o => !o);
            return;
          }

          if (k === "Escape") {
            setOpen(false);
            return;
          }
        };

        return (
          <Container>
            <Row>
              <Col className="mt-4">
                <Form.Group>
                  <Row>
                    {label && (
                      <Form.Label className={boldLabel ? "fw-bold ps-0" : undefined}>
                        {label} {required && <span className="input-required-marker">*</span>}
                      </Form.Label>
                    )}
                  </Row>
                  <Row>
                    <div
                      ref={dropdownRef}
                      tabIndex={tabIndex}
                      className={`dropdown ${!selectedId && !isOpen ? "is-placeholder" : ""}`}
                      style={{ width, maxWidth: width, height, maxHeight: height }}
                      aria-expanded={isOpen}
                      aria-haspopup="listbox"
                      aria-controls={`${name}-listbox`}
                      onFocus={() => setOpen(true)}
                      onBlur={field.onBlur}
                      onKeyDown={handleKeyDown}
                    >
                      <div
                        className="dropdown-header"
                        onClick={toggle}
                        style={{ width, maxWidth: width, minWidth: width, height }}
                      >
                        {selectedLabel}
                        <FontAwesomeIcon style={{ marginLeft: 5 }} icon={isOpen ? faChevronUp : faChevronDown} />
                      </div>
                      <div
                        id={`${name}-listbox`}
                        className={`dropdown-body ${isOpen ? "open" : ""}`}
                        style={{ width: "100%", maxWidth: width, minWidth: width }}
                      >
                        {options.map((item, idx) => (
                          <div
                            key={item.id}
                            ref={(el) => (dropdownItemsRef.current[idx] = el)}
                            id={String(item.id)}
                            className={`dropdown-item ${item.id === selectedId ? "selected" : ""}`}
                            aria-selected={item.id === selectedId}
                            onClick={() => selectById(item.id)}
                          >
                            {item.label}
                          </div>
                        ))}
                      </div>
                    </div>
                    {fieldState.error && (
                      <div className="text-input-error-message ps-0">{fieldState.error.message}</div>
                    )}
                  </Row>
                </Form.Group>
              </Col>
            </Row>
          </Container>
        );
      }}
    />
  );
});

export default Dropdown;
